import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import heroImage1x from './images/watch-stream-about-hero.jpg';
import heroImage2x from './images/watch-stream-about-hero@2x.jpg';

import chatOnlyImage1x from './images/streams-chat-only.jpg';
import charOnlyImage2x from './images/streams-chat-only@2x.jpg';

import mobileImage1x from './images/streams-mobile.jpg';
import mobileImage2x from './images/streams-mobile@2x.jpg';

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className='about'>
      <Helmet>
        <title>About Watch Streams - Multistream Viewer for Twitch, YouTube & Kick</title>
        <meta name="description" content="Learn more about Watch Streams, the ultimate multistream viewer for Twitch, YouTube and Kick." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Watch Streams",
            "applicationCategory": "MultimediaApplication",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "description": "The ultimate multistream viewer for Twitch, YouTube and Kick. Watch multiple live streams at once, manage chats, and stay connected to your favorite streamers.",
            "operatingSystem": "Web Browser",
            "browserRequirements": "Requires JavaScript. Requires HTML5.",
            "url": `${window.location.origin}`,
            "featureList": [
              "Watch Twitch, YouTube and Kick streams simultaneously",
              "Flexible layouts",
              "Chat control and chat-only mode",
              "Focus mode",
              "Full mobile web support"
            ],
            "screenshot": `${window.location.origin}/assets/social-banner.png`
          })}
        </script>
      </Helmet>
      <section>
        <h1>About Watch Streams, the multistream viewer for Twitch, YouTube & Kick</h1>
        <p><strong>Watch Streams</strong> is your ultimate multistream viewer for <strong><a href="https://www.twitch.tv" className='twitch' target='_blank' rel='noreferrer'>Twitch</a></strong>, <strong><a href="https://www.youtube.com/live" className='youtube' target='_blank' rel='noreferrer'>YouTube Live</a></strong> and <strong><a href="https://www.kick.com" className='kick' target='_blank' rel='noreferrer'>Kick</a></strong>. Seamlessly watch multiple live streams at once, manage chats, and stay connected to your favorite streamers—all in one powerful platform.</p>
      </section>

      <section className='gallery'>
        <img
          src={heroImage1x}
          srcSet={`${heroImage1x} 1x, ${heroImage2x} 2x`}
          alt="Watch Streams multistream viewer showing multiple Twitch, YouTube and Kick live streams on desktop with chat windows"
          title='Watch Streams Desktop Multistream View'
        />
      </section>

      <section>
        <p>Whether you're a dedicated fan, an engaged viewer, or a proactive streamer, Watch Streams is designed to enhance your live-streaming experience. Say goodbye to switching between tabs or missing out on the action—our intuitive service brings all your favorite live broadcasts into one convenient screen.</p>
      </section>

      <section>
        <h2>Key Features of Watch Streams</h2>
        <ul>
          <li>
            <h3>Watch Twitch, YouTube and Kick Simultaneously</h3>
            <p>Effortlessly view live streams from Twitch, YouTube and Kick side-by-side. Perfect for gaming marathons, live events, or keeping up with your favorite creators across platforms.</p>
          </li>
          <li>
            <h3>Flexible Layouts</h3>
            <p>Customize your screen to prioritize the streams that matter most. Adjust layouts to suit your needs, whether you're multitasking or fully immersed in a single stream.</p>
          </li>
          <li>
            <h3>Chat Control</h3>
            <ul>
              <li><strong>Hide Chat:</strong> Minimize distractions by hiding chat for one or all streams.</li>
              <li><strong>Chat-Only Mode:</strong> Moderators and broadcasters can focus on engaging with their communities while keeping streams in the background. Manage multiple chat rooms without breaking a sweat.</li>
            </ul>
          </li>
          <li>
            <h3>Focus Mode</h3>
            <p>Need to zero in on one stream? Use Focus Mode to expand a single video while keeping all chats accessible. Switching focus to another stream is just a click away.</p>
          </li>
          <li>
            <h3>Full Mobile Web Support</h3>
            <p>Take Watch Streams on the go. Our mobile-optimized platform ensures smooth performance on your phone or tablet, so you never miss a moment—no matter where you are.</p>
          </li>
        </ul>
      </section>

      <section className='gallery'>
        <div className='gallery-two'>
          <img
            src={chatOnlyImage1x}
            srcSet={`${chatOnlyImage1x} 1x, ${charOnlyImage2x} 2x`}
            alt="Watch Streams chat-only mode displaying multiple Twitch, YouTube and Kick chats for moderators and broadcasters"
            title='Watch Streams Chat-Only Mode for Moderators'
          />
          <img
            src={mobileImage1x}
            srcSet={`${mobileImage1x} 1x, ${mobileImage2x} 2x`}
            alt="Watch Streams mobile view showing Twitch, YouTube and Kick streams on phone with responsive layout"
            title='Watch Streams Mobile Multistream Viewer'
          />
        </div>
      </section>

      <section>
        <h2>For Broadcasters and Moderators</h2>
        <p>Watch Streams isn't just for viewers—it's an essential tool for Twitch, YouTube and Kick broadcasters. Whether you're managing your community or collaborating with other creators, our platform simplifies multitasking and streamlining communication.</p>
        <ul>
          <li><strong>Monitor Multiple Chats Simultaneously:</strong> With Chat-Only Mode, track all your chat rooms across Twitch, YouTube and Kick without opening multiple tabs or devices. It's efficient and distraction-free.</li>
          <li><strong>Collaborate with Other Broadcasters:</strong> Display your co-streamers' feeds and chats alongside your own. Monitor and engage with all participants in real time.</li>
          <li><strong>Rebroadcast and Stay in Control:</strong> Use Focus Mode to spotlight teammates, competitors, or event segments while keeping chats visible and manageable.</li>
        </ul>
      </section>

      <section>
        <h2>Why Choose Watch Streams?</h2>
        <p><strong>Watch Streams</strong> isn't just a multistream viewer; it's your go-to tool for enjoying <strong>Twitch</strong>, <strong>YouTube Live</strong> and <strong>Kick</strong> streams like never before. We prioritize convenience for both viewers and broadcasters, enabling you to stay connected with multiple communities while retaining full control of your viewing experience.</p>
        <p>Whether you're following esports tournaments, hosting virtual watch parties, or collaborating with fellow streamers, Watch Streams puts everything at your fingertips.</p>
        <p><strong>Experience the best way to watch Twitch, YouTube and Kick live streams together. Join the Watch Streams community today and elevate your multistreaming experience!</strong></p>
      </section>


      <section className='get-started'>
        <button
          className="button primary"
          onClick={() => navigate('/')}
        >
          Get Started
        </button>
      </section>
    </div>
  );
};

export default AboutPage;
